import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import Dropdown from '@/components/ui/Dropdown';


const UniversityRepeatableField = ({ fieldIndex, field, isRequired, displayMode = false, initialValue = null }) => {
  const [universities, setUniversities] = useState([{ id: 1, university: '', degree: '', fieldOfStudy: '', graduationYear: '', gpa: '' }]);


  // Initialize with existing data if in display mode
  useEffect(() => {
    if (displayMode && initialValue) {
      try {
        const parsedData = typeof initialValue === 'string' ? JSON.parse(initialValue) : initialValue;
        if (Array.isArray(parsedData)) {
          setUniversities(parsedData);
        }
      } catch (error) {
        console.error('Error parsing university data:', error);
      }
    }
  }, [displayMode, initialValue]);

  // Use custom university options from field configuration, or fallback to defaults
  const universityOptions = field?.universityOptions && Array.isArray(field.universityOptions) && field.universityOptions.length > 0 
    ? field.universityOptions.map(option => ({ label: option, value: option }))
    : field?.options && Array.isArray(field.options) && field.options.length > 0 
    ? field.options.map(option => ({ label: option, value: option }))
    : [
        { label: 'University of Oxford', value: 'University of Oxford' },
        { label: 'University of Cambridge', value: 'University of Cambridge' },
        { label: 'Imperial College London', value: 'Imperial College London' },
        { label: 'University College London', value: 'University College London' },
        { label: 'London School of Economics', value: 'London School of Economics' },
        { label: 'University of Edinburgh', value: 'University of Edinburgh' },
        { label: 'King\'s College London', value: 'King\'s College London' },
        { label: 'University of Manchester', value: 'University of Manchester' },
        { label: 'University of Bristol', value: 'University of Bristol' },
        { label: 'University of Warwick', value: 'University of Warwick' },
        { label: 'University of Glasgow', value: 'University of Glasgow' },
        { label: 'University of Birmingham', value: 'University of Birmingham' },
        { label: 'University of Sheffield', value: 'University of Sheffield' },
        { label: 'University of Leeds', value: 'University of Leeds' },
        { label: 'University of Nottingham', value: 'University of Nottingham' },
        { label: 'University of Southampton', value: 'University of Southampton' },
        { label: 'University of York', value: 'University of York' },
        { label: 'University of Durham', value: 'University of Durham' },
        { label: 'University of Exeter', value: 'University of Exeter' },
        { label: 'University of Bath', value: 'University of Bath' },
      ];

  // Use custom degree options from field configuration, or fallback to defaults
  const degreeOptions = field?.degreeOptions && Array.isArray(field.degreeOptions) && field.degreeOptions.length > 0 
    ? field.degreeOptions.map(option => ({ label: option, value: option }))
    : [
        { label: 'Bachelor\'s Degree', value: 'Bachelor\'s Degree' },
        { label: 'Master\'s Degree', value: 'Master\'s Degree' },
        { label: 'PhD/Doctorate', value: 'PhD/Doctorate' },
        { label: 'Associate Degree', value: 'Associate Degree' },
        { label: 'Diploma', value: 'Diploma' },
        { label: 'Certificate', value: 'Certificate' },
        { label: 'Other', value: 'Other' }
      ];

  // Get custom field labels or use defaults
  const getFieldLabel = (labelKey, defaultLabel) => {
    return field?.fieldLabels?.[labelKey] || defaultLabel;

  };

  // Use custom field of study options from field configuration, or fallback to defaults
  const fieldOfStudyOptions = field?.fieldOfStudyOptions && Array.isArray(field.fieldOfStudyOptions) && field.fieldOfStudyOptions.length > 0
    ? field.fieldOfStudyOptions.map(option => ({ label: option, value: option }))
    : [
        { label: 'Computer Science', value: 'Computer Science' },
        { label: 'Engineering', value: 'Engineering' },
        { label: 'Business Administration', value: 'Business Administration' },
        { label: 'Economics', value: 'Economics' },
        { label: 'Mathematics', value: 'Mathematics' },
        { label: 'Physics', value: 'Physics' },
        { label: 'Chemistry', value: 'Chemistry' },
        { label: 'Biology', value: 'Biology' },
        { label: 'Medicine', value: 'Medicine' },
        { label: 'Law', value: 'Law' },
        { label: 'Psychology', value: 'Psychology' },
        { label: 'Sociology', value: 'Sociology' },
        { label: 'History', value: 'History' },
        { label: 'English Literature', value: 'English Literature' },
        { label: 'Philosophy', value: 'Philosophy' },
        { label: 'Art & Design', value: 'Art & Design' },
        { label: 'Music', value: 'Music' },
        { label: 'Other', value: 'Other' }
      ];

  const addUniversity = () => {
    if (universities.length >= 10) {
      return; // Don't add more if limit reached
    }
    const newId = Math.max(...universities.map(u => u.id)) + 1;
    setUniversities([...universities, { 
      id: newId, 
      university: '', 
      degree: '', 
      fieldOfStudy: '', 
      graduationYear: '', 
      gpa: '' 
    }]);
  };

  const removeUniversity = (id) => {
    if (universities.length > 1) {
      setUniversities(universities.filter(u => u.id !== id));
    }
  };

  const moveUniversityUp = (index) => {
    if (index > 0) {
      const newUniversities = [...universities];
      [newUniversities[index], newUniversities[index - 1]] = [newUniversities[index - 1], newUniversities[index]];
      setUniversities(newUniversities);
    }
  };

  const moveUniversityDown = (index) => {
    if (index < universities.length - 1) {
      const newUniversities = [...universities];
      [newUniversities[index], newUniversities[index + 1]] = [newUniversities[index + 1], newUniversities[index]];
      setUniversities(newUniversities);
    }
  };

  const updateUniversity = (id, field, value) => {
    setUniversities(universities.map(u => 
      u.id === id ? { ...u, [field]: value } : u
    ));
  };



  // Display mode - show data in a structured format
  if (displayMode) {
    return (
      <div className="space-y-4">
        {universities.map((university, index) => (
          <div key={university.id} className="bg-muted rounded-lg p-4 border border-border">
            <h3 className="font-semibold text-lg mb-3 text-foreground">
              {index + 1}. University Education
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {university.university && (
                <div>
                  <h4 className="font-medium text-foreground mb-1">{getFieldLabel('universityLabel', 'University Name')}</h4>
                  <p className="text-foreground bg-card px-3 py-2 rounded border">{university.university}</p>
                </div>
              )}
              {university.degree && (
                <div>
                  <h4 className="font-medium text-foreground mb-1">{getFieldLabel('degreeLabel', 'Qualification')}</h4>
                  <p className="text-foreground bg-card px-3 py-2 rounded border">{university.degree}</p>
                </div>
              )}
              {university.fieldOfStudy && (
                <div>
                  <h4 className="font-medium text-foreground mb-1">{getFieldLabel('fieldOfStudyLabel', 'Subject')}</h4>
                  <p className="text-foreground bg-card px-3 py-2 rounded border">{university.fieldOfStudy}</p>
                </div>
              )}
              {university.graduationYear && (
                <div>
                  <h4 className="font-medium text-foreground mb-1">{getFieldLabel('graduationYearLabel', 'Graduation Year')}</h4>
                  <p className="text-foreground bg-card px-3 py-2 rounded border">{university.graduationYear}</p>
                </div>
              )}
              {university.gpa && (
                <div>
                  <h4 className="font-medium text-foreground mb-1">{getFieldLabel('gpaLabel', 'GPA')}</h4>
                  <p className="text-foreground bg-card px-3 py-2 rounded border">{university.gpa}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

 

  // Edit mode - show form inputs
  return (
    <div>
      <div className="space-y-6">
        {universities.map((university, index) => (
          <div key={university.id} className="bg-card rounded-lg">
            <div className="flex justify-between items-center ">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-foreground">
               
                </span>
              </div>
              {universities.length > 1 && (
              <div className="flex items-center gap-2 py-2">
                <button
                  type="button"
                  onClick={() => moveUniversityUp(index)}
                  disabled={index === 0}
                  className={`p-2 rounded-lg ${
                    index === 0 
                      ? 'text-muted-foreground cursor-not-allowed' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  title="Move up"
                >
                  <ArrowUp size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => moveUniversityDown(index)}
                  disabled={index === universities.length - 1}
                  className={`p-2 rounded-lg ${
                    index === universities.length - 1 
                      ? 'text-muted-foreground cursor-not-allowed' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  title="Move down"
                >
                  <ArrowDown size={18} />
                </button>
              </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-foreground text-base font-semibold mb-2">
                  {getFieldLabel('universityLabel', 'University Name')} <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  options={universityOptions}
                  value={university.university ? { label: university.university, value: university.university } : null}
                  onChange={(selected) => updateUniversity(university.id, 'university', selected?.value || '')}
                  placeholder="Select university"
                  className="mt-1 border border-input text-muted-foreground rounded-md"
                  triggerClassName="!bg-transparent"
                  searchable={true}
                />
              </div>

              <div>
                <label className="block text-foreground text-base font-semibold mb-2">
                  {getFieldLabel('degreeLabel', 'Qualification')} <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  options={degreeOptions}
                  value={university.degree ? { label: university.degree, value: university.degree } : null}
                  onChange={(selected) => updateUniversity(university.id, 'degree', selected?.value || '')}
                  placeholder="Select degree"
                  className="mt-1 border border-input text-muted-foreground rounded-md"
                  triggerClassName="!bg-transparent"
                  searchable={true}
                />
              </div>

              <div>
                <label className="block text-foreground text-base font-semibold mb-2">
                  {getFieldLabel('fieldOfStudyLabel', 'Subject')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={university.fieldOfStudy}
                  onChange={(e) => updateUniversity(university.id, 'fieldOfStudy', e.target.value)}
                  placeholder="Enter subject/field of study"
                    className="w-full px-3 py-[12px] rounded-lg border border-border focus:outline-none focus:border-input bg-card"
                  required={isRequired}
                />
              </div>

              <div>
                <label className="block text-foreground text-base font-semibold mb-2">
                  {getFieldLabel('graduationYearLabel', 'Graduation Year')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1950"
                  max={new Date().getFullYear() + 10}
                  value={university.graduationYear}
                  onChange={(e) => updateUniversity(university.id, 'graduationYear', e.target.value)}
                  placeholder="e.g., 2023"
                  className="w-full px-3 py-[12px] rounded-lg border border-border focus:outline-none focus:border-input bg-card"
                  required={isRequired}
                />
              </div>

              <div>
                <label className="block text-foreground text-base font-semibold mb-2">
                  {getFieldLabel('gpaLabel', 'GPA (Optional)')}
                </label>
                <input
                  type="number"
                  min="0"
                  max="4"
                  step="0.01"
                  value={university.gpa}
                  onChange={(e) => updateUniversity(university.id, 'gpa', e.target.value)}
                  placeholder="e.g., 3.8"
                    className="w-full px-3 py-[12px] rounded-lg border border-border focus:outline-none focus:border-input bg-card"
                />
              </div>
            </div>
            {/* Add horizontal rule after each university entry except the last one */}
            {index < universities.length - 1 && <hr className="mt-5 -mb-6" />}
          </div>
        ))}
      </div>
      {/* Add university and delete university buttons below the fields */}
      <div className="flex flex-row items-center justify-between mt-4">
        <button
          type="button"
          onClick={addUniversity}
          disabled={universities.length >= 10}
          className={`text-base border ${universities.length >= 10 ? 'bg-primary-dark hover:bg-primary-dark cursor-not-allowed' : 'bg-primary-dark hover:bg-dark/80'} text-white py-2 px-4 rounded-lg`}
          title={universities.length >= 10 ? "Maximum limit of 10 universities reached" : "Add another university"}
        >
          Add university {universities.length >= 10 ? "(Max limit reached)" : ""}
        </button>
        {universities.length > 1 && (
          <button
            type="button"
            onClick={() => removeUniversity(universities[universities.length - 1].id)}
            className="text-white bg-red-500 hover:bg-red-700 py-2 px-4 rounded-lg"
            title="Delete  anotheruniversity"
          >
            Delete university
          </button>
        )}
      </div>
      {/* Hidden inputs to store the universities data */}
      <input
        type="hidden"
        name={`fields[${fieldIndex}][field_value]`}
        value={JSON.stringify(universities.map(u => u.university).filter(name => name && name.trim() !== ''))}
        required={isRequired}
      />
            {/* Multiple hidden inputs for sub_fields */}
      {(() => {
        const sub_fields = [];

        universities.forEach((university, universityIndex) => {
          // All 5 fields for this university will have the same index (universityIndex)
          
          // University Name field
          if (university.university && university.university.trim() !== '') {
            sub_fields.push({
              value: university.university,
              label: getFieldLabel('universityLabel', 'University Name'),
              index: universityIndex
            });
          }

          // Degree/Qualification field
          if (university.degree && university.degree.trim() !== '') {
            sub_fields.push({
              value: university.degree,
              label: getFieldLabel('degreeLabel', 'Qualification'),
              index: universityIndex
            });
          }

          // Field of Study field
          if (university.fieldOfStudy && university.fieldOfStudy.trim() !== '') {
            sub_fields.push({
              value: university.fieldOfStudy,
              label: getFieldLabel('fieldOfStudyLabel', 'Subject'),
              index: universityIndex
            });
          }

          // Graduation Year field
          if (university.graduationYear && university.graduationYear.trim() !== '') {
            sub_fields.push({
              value: university.graduationYear,
              label: getFieldLabel('graduationYearLabel', 'Graduation Year'),
              index: universityIndex
            });
          }

          // GPA field
          if (university.gpa && university.gpa.trim() !== '') {
            sub_fields.push({
              value: university.gpa,
              label: getFieldLabel('gpaLabel', 'GPA'),
              index: universityIndex
            });
          }
        });

        return sub_fields.map((subField, index) => (
          <React.Fragment key={index}>
            <input
              type="hidden"
              name={`fields[${fieldIndex}][sub_fields][${index}][value]`}
              value={subField.value}
            />
            <input
              type="hidden"
              name={`fields[${fieldIndex}][sub_fields][${index}][label]`}
              value={subField.label}
            />
            <input
              type="hidden"
              name={`fields[${fieldIndex}][sub_fields][${index}][index]`}
              value={subField.index}
            />
          </React.Fragment>
        ));
      })()}
      </div>
  );
};

export default UniversityRepeatableField; 